import {InMemoryCache} from '@apollo/client';
import introspectionToPossibleTypes from './introspectionToPossibleTypes';
import dataIdFromObject from './dataIdFromObject';
// eslint-disable-next-line import/extensions
import introspectionQueryResultData from '../../gql/fragmentTypes.json';

export const possibleTypes = introspectionToPossibleTypes(introspectionQueryResultData);

const createApolloCache = (): InMemoryCache =>
  new InMemoryCache({
    possibleTypes,
    dataIdFromObject,
    typePolicies: {
      Caterer: {
        fields: {
          publicCatererFulfillmentReportCard: {
            merge(existing, incoming, {mergeObjects}) {
              return mergeObjects(existing, incoming);
            },
          },
          advanceNotice: {
            merge(_existing, incoming) {
              return incoming;
            },
          },
        },
      },
      ConsumerCart: {
        fields: {
          customerMessages: {
            merge(_existing, incoming) {
              return incoming;
            },
          },
          items: {
            merge(_existing, incoming) {
              return incoming;
            },
          },
          lineItems: {
            merge(_existing, incoming) {
              return incoming;
            },
          },
        },
      },
      FulfillmentDetail: {
        fields: {
          customerMessages: {
            merge(_existing, incoming) {
              return incoming;
            },
          },
        },
      },
      MenuItemSalesAnalysis: {
        keyFields: ['id', 'itemRevenue', 'itemsSold', 'ordersWithItem'],
      },
      Query: {
        fields: {
          constants: {
            merge(existing, incoming, {mergeObjects}) {
              return mergeObjects(existing, incoming);
            },
          },
          dmDeliveries: {
            merge(_existing, incoming) {
              // This type policy is here to help the hydration as exisiting can be null on first load
              return incoming;
            },
          },
        },
      },
      CatererAccount: {
        fields: {
          performance: {
            merge(existing, incoming, {mergeObjects}) {
              // This type policy merges incoming data with existing data. helps with hydration issues from react.
              return mergeObjects(existing, incoming);
            },
          },
          stores: {
            merge(existing, incoming, {mergeObjects}) {
              return mergeObjects(existing, incoming);
            },
          },
          supplierMarketingCampaigns: {
            merge(existing, incoming, {mergeObjects}) {
              // This type policy merges incoming data with existing data. helps with hydration issues from react.
              return mergeObjects(existing, incoming);
            },
          },
          orders: {
            keyArgs: [
              'filter',
              'sourceTypes',
              'startDate',
              'endDate',
              'catererId',
              'personalStoreGroupId',
              'searchString',
            ],
            // eslint-disable-next-line consistent-return
            read(
              existing,
              {variables: {offset = 0, limit = existing?.edges?.length, name}}: any = {}
            ) {
              // A read function should always return undefined if existing is
              // undefined. Returning undefined signals that the field is
              // missing from the cache, which instructs Apollo Client to
              // fetch its value from your GraphQL server.
              // console.log(existing, rest, offset + limit, offset, limit, existing?.length);
              if (existing) {
                if (name || existing.nodes) {
                  return {...existing};
                }

                const data = existing.edges.slice(offset, offset + limit);
                const dataLength = data.filter(Boolean).length;
                const needsMoreData = dataLength < limit;

                return (
                  !!dataLength &&
                  !needsMoreData && {
                    totalCount: existing.totalCount,
                    edges: data,
                  }
                );
              }
            },

            merge(existing, incoming, {args: {offset = 0}}: any) {
              if (!existing || incoming.nodes) return {...incoming};
              const {edges} = existing;
              if (existing.totalCount !== incoming.totalCount) return {...incoming};
              const merged = edges.slice(0);
              for (let i = 0; i < incoming.edges.length; ++i) {
                merged[offset + i] = incoming.edges[i];
              }
              return {edges: merged, totalCount: incoming.totalCount};
            },
          },
        },
      },
      StorePerformanceStore: {
        // Never cache a normalized StorePerformanceStore object, rely on parent field instead
        // as the parent field is typically a connection field which contains args for things like time frame
        // and different timeframes will return the same id for each store but different performance data
        keyFields: false,
      },
    },
  });

export default createApolloCache;
